import React, { useContext, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import styled from 'styled-components'
import { LEGAL_FORMALITY_DOCUMENT_UPLOAD_MUTATION } from 'graphql/LegalFormalities'
import { Button, Upload } from 'antd'
import { UploadFile, UploadFileStatus } from 'antd/lib/upload/interface'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { LegalFormalityDocumentContext } from 'contexts/LegalFormalityDocumentContext'
import { AbilityContext } from '../../../../../contexts/abilityContext'

interface IUploadVars {
  purchaseId: number
  documentType: string
  fileRecto?: string
  fileVerso?: string
  removedUpload?: string
  legalDocumentableType?: string
  legalDocumentableId?: string
}
const versionUid = (version: string) => (version == 'Recto' ? '-2' : '-1')
const fileToListItem = (
  version: string,
  url: string | undefined,
  status: UploadFileStatus,
  opts = {}
) => {
  const file = {} as UploadFile
  Object.assign(file, {
    uid: versionUid(version),
    name: version,
    status: status,
    url: url,
    thumbUrl: url,
    ...opts,
  })
  return file
}

const Uploads = () => {
  const { legalFormality, legalFormalityRefetch } = useContext(
    CurrentLegalFormalityContext
  )
  const { selectedDocumentListItem, currentDocument, setCurrentDocument } =
    useContext(LegalFormalityDocumentContext)
  const ability: any = useContext(AbilityContext)
  const formalistOnly = ability.can('update', 'LegalFormality')

  const versions = ['Recto', 'Verso']

  // setup fileList if already uploaded files for document
  const allreadyUploadedFiles = () => {
    const files = [] as UploadFile[]
    currentDocument &&
      versions.map(version => {
        const fileVersion = 'file' + version
        const url = currentDocument[fileVersion] as string | null

        url && files.push(fileToListItem(version, url, 'done'))
      })

    return files
  }
  const [uploadDocument] = useMutation(LEGAL_FORMALITY_DOCUMENT_UPLOAD_MUTATION)
  const [fileList, setFileList] = useState(
    allreadyUploadedFiles() as UploadFile[]
  )

  useEffect(() => {
    setFileList(allreadyUploadedFiles())
  }, [
    selectedDocumentListItem,
    currentDocument?.fileRecto,
    currentDocument?.fileVerso,
  ])

  const pushFileListItem = (item: UploadFile) => {
    const newFileList = [...fileList].filter(e => e.uid != item.uid)
    newFileList.push(item)
    setFileList(newFileList)
  }

  const uploadVars = () => {
    if (!selectedDocumentListItem) return {} as IUploadVars

    const variables = {
      purchaseId: parseInt(legalFormality.purchase.id),
      documentType: selectedDocumentListItem.documentType,
    } as IUploadVars

    selectedDocumentListItem.target &&
      (variables.legalDocumentableType = selectedDocumentListItem.target?.type)
    selectedDocumentListItem.target &&
      (variables.legalDocumentableId = selectedDocumentListItem.target?.id)

    return variables
  }

  const upload = (e: any) => {
    const field = fileList.find(d => d.uid == '-2' && d.status == 'done')
      ? 'fileVerso'
      : 'fileRecto'
    const vars: IUploadVars = uploadVars()
    vars[field] = e.file

    return uploadDocument({ variables: vars }).then(uploadCallback)
  }

  const uploadCallback = (data: any) => {
    const doc = data.data.legalFormalityDocumentUpload.legalFormalityDocument
    setCurrentDocument(doc)
    legalFormalityRefetch()
  }

  const beforeUpload = () => {
    const type = fileList.length == 1 ? 'Verso' : 'Recto'
    pushFileListItem(
      fileToListItem(type, undefined, 'uploading', { percent: 50 })
    )
  }

  const handleRemoveUpload = (file: UploadFile) => {
    const fieldName = file.uid == '-1' ? 'fileVerso' : 'fileRecto'
    const vars: IUploadVars = uploadVars()
    vars.removedUpload = fieldName
    return uploadDocument({ variables: vars }).then(uploadCallback)
  }

  const uploadBtn = () => {
    if (!formalistOnly) return false
    if (fileList.length == 2) return false

    return (
      <Button>
        {fileList.filter(f => f.uid == '-2').length == 0
          ? 'Upload Recto'
          : 'Upload Verso'}
      </Button>
    )
  }

  return (
    <Container>
      <Upload
        listType="picture-card"
        fileList={fileList}
        customRequest={upload}
        defaultFileList={allreadyUploadedFiles()}
        beforeUpload={beforeUpload}
        showUploadList={{
          showPreviewIcon: false,
          showRemoveIcon: formalistOnly,
          showDownloadIcon: true,
        }}
        onRemove={file => handleRemoveUpload(file)}
      >
        {uploadBtn()}
      </Upload>
    </Container>
  )
}

const Container = styled.div`
  margin-top: 16px;
`

export default Uploads
